<template>
  <div>
    <StaticDataTable class="static-table-list table-grid-lines">
      <template v-slot:header>
        <tr>
          <th>Stock</th>
          <th>Size</th>
          <th>Supplier</th>
          <th><Tooltip label="Supplier lead time in calendar days" helpStyle>Lead Time</Tooltip></th>
          <th>Container</th>
          <th>Block Size</th>
          <th>On Hand</th>
          <th><Tooltip label="Average daily stock usage for business days" helpStyle>Daily Usage</Tooltip></th>
          <th><Tooltip label="Minimum business days of stock available required" helpStyle>Required Days Of Stock</Tooltip></th>
          <th><Tooltip label="Minimum stock level based on required days of stock and usage" helpStyle>Required Stock Level</Tooltip></th>
          <th><Tooltip label="Business Days Of Stock Remaining" helpStyle>Days Of Stock Remaining</Tooltip></th>

          <th v-for="monthItem in incomingMonthList" :key="`incoming-month-${monthItem.month}-${monthItem.year}`">
            <Tooltip :label="`Total incoming sheets for the size expected to arrive during the month of ${monthItem.label}`" helpStyle>Inc. {{monthItem.label}}</Tooltip>
          </th>

          <th>Next Incoming</th>
          <th>Projected Required Stock Date Met</th>
          <th>Purchasing Required</th>
          <th>Last Purchase Date</th>
          <th>Est. PO Arrival</th>
          <th><Tooltip label="Minimum number of sheets to order to meet minimum stock required" helpStyle>Min. Sheets Req.</Tooltip></th>
          <th><Tooltip label="Minimum number of blocks to order to meet minimum stock required" helpStyle>Min. Blocks Req.</Tooltip></th>
          <th>Info</th>
        </tr>
      </template>
      <template v-slot:body>
        <tr v-for="stockPurchaseInfo of stockPurchasingList" :key="stockPurchaseInfo.id" :class="getPurchaseRequiredRowClass(stockPurchaseInfo)">
          <td>
            <LinkStock :name="stockPurchaseInfo.stockName" :id="stockPurchaseInfo.stockId" />
          </td>
          <td>{{stockPurchaseInfo.height}} x {{stockPurchaseInfo.width}}</td>
          <td>
            <LinkContact :id="stockPurchaseInfo.preferredSupplierId" :name="stockPurchaseInfo.preferredSupplierName" />
          </td>
          <td>{{stockPurchaseInfo.preferredSupplierLeadDays}}</td>
          <td>
            <a href="#" @click.prevent="displayContainerInfo($event, stockPurchaseInfo)" class="link-displayinfo">
              {{stockPurchaseInfo.stockContainerCode}}
            </a>
          </td>
          <td>{{stockPurchaseInfo.blockSize}}</td>
          <td>{{stockPurchaseInfo.onHandTotalSheets}}</td>
          <td>{{stockPurchaseInfo.averageDailyStockUsage}}</td>
          <td>{{stockPurchaseInfo.requiredDaysOfStock}}</td>
          <td>{{stockPurchaseInfo.requiredStockLevel}}</td>
          <td>
            <span v-if="stockPurchaseInfo.averageDailyStockUsage > 0">{{formatInteger(stockPurchaseInfo.onHandTotalSheets / stockPurchaseInfo.averageDailyStockUsage)}}</span>
            <span v-else>N/A</span>
          </td>
          <td v-for="monthItem in incomingMonthList" :key="`incoming-month-${monthItem.month}-${monthItem.year}`">
            <a href="#" v-if="stockTotalSheetsIncomingForMonth(monthItem.date, stockPurchaseInfo) > 0" @click.prevent="displayIncomingStock($event, monthItem.date, stockPurchaseInfo)" class="link-displayinfo">
              {{stockTotalSheetsIncomingForMonth(monthItem.date, stockPurchaseInfo)}}
            </a>
            <span v-else>0</span>
          </td>
          <td>{{formatDate(stockPurchaseInfo.incomingNextExpectedDate)}}</td>
          <td>{{formatDate(stockPurchaseInfo.hitRequiredStockDate)}}</td>
          <td>
            <span :class="getPurchaseRequiredLabelClass(stockPurchaseInfo)">
              <i :class="['far', getPurchaseRequiredIconClass(stockPurchaseInfo)]" v-if="getPurchaseRequiredIconClass(stockPurchaseInfo)"></i>
              {{getPurchaseRequiredText(stockPurchaseInfo)}}
            </span>
          </td>
          <td>
            <span v-if="(isDateToday(stockPurchaseInfo.requiredPOCreationDate) || isDateBeforeToday(stockPurchaseInfo.requiredPOCreationDate)) && stockPurchaseInfo.requiredPOCreationDate">Now</span>
            <span v-else-if="stockPurchaseInfo.requiredPOCreationDate">{{formatDate(stockPurchaseInfo.requiredPOCreationDate)}}</span>
          </td>
          <td>
            <span v-if="stockPurchaseInfo.estimatedPOArrivalDate">{{formatDate(stockPurchaseInfo.estimatedPOArrivalDate)}}</span>
            <span v-else>N/A</span>
          </td>
          <td>{{stockPurchaseInfo.minSheetsToOrder}}</td>
          <td>{{stockPurchaseInfo.minBlocksToOrder}}</td>
          <td>
            <FormButton class="btn-xs btn-default" @click.prevent="displayStockLevelChart($event, stockPurchaseInfo)">
              <i class="far fa-chart-line"></i> Chart
            </FormButton>
          </td>
        </tr>
      </template>
      <template v-slot:empty v-if="stockPurchasingList.length == 0">No reporting items available</template>
    </StaticDataTable>

    <Popover ref="containerInfo" placement="right" :showHeader="true" headerTitle="Container Information" sizeClass="popover-large">
      <div class="p-2">
        <div class="font-weight-bold">
          Container
        </div>
        <div>{{selectedContainerInfo ? selectedContainerInfo.stockContainerName : ''}}</div>

        <div class="font-weight-bold">
          Container Code
        </div>
        <div>{{selectedContainerInfo ? selectedContainerInfo.stockContainerCode : ''}}</div>

        <div class="font-weight-bold">
          Blocks per Container
        </div>
        <div>{{selectedContainerInfo ? selectedContainerInfo.stockContainerBlockQuantity : ''}}</div>
      </div>
    </Popover>

    <Popover ref="incomingStockPopover" placement="right" :showHeader="true" :headerTitle="selectedIncomingInfo.title" sizeClass="popover-xxlarge" :scrollable="true">
      <div class="p-2">
        <StaticDataTable class="static-table-list" smallRows>
          <template v-slot:header>
            <tr>
              <th>Quantity</th>
              <th>Purchase Order</th>
              <th>Status</th>
              <th>Supplier</th>
              <th>Expected Date</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="poItem of selectedIncomingItemList" :key="poItem.id">
              <td>{{poItem.totalSheets}}</td>
              <td><LinkPurchaseOrder :id="poItem.purchaseOrder.id" :name="poItem.purchaseOrder.sequenceNumber" /></td>
              <td>
                <LabelBadge :labelClass="`label-po-status-${poItem.purchaseOrder.status}`">{{poItem.purchaseOrder.status | formatLookupName(PurchaseOrderStatusTypeLookup)}}</LabelBadge>
              </td>
              <td><LinkContact :id="poItem.purchaseOrder.contact.id" :name="poItem.purchaseOrder.contact.name" /></td>
              <td>{{formatDate(poItem.purchaseOrder.dateExpected)}}</td>
            </tr>
          </template>
          <template v-slot:empty v-if="selectedIncomingItemList.length == 0">No incoming stock available</template>
        </StaticDataTable>
      </div>
    </Popover>

    <Popover ref="stockLevelChartPopover" placement="right" :showHeader="true" :headerTitle="selectedChartInfo.title" sizeClass="popover-xxlarge" :scrollable="true">
      <div>

        <InventoryStockLevelChartWidget
          v-if="selectedChartInfo.stockId"
          :embedded="true"
          :displayFilter="false"
          :popupEnabled="false"
          :exportEnabled="false"
          :stockFilterEnabled="false"
          :gridHeight="330"
          :glassSizeId="selectedChartInfo.glassSizeId"
          :stockId="selectedChartInfo.stockId"
          :startDate="selectedChartInfo.startDate"
          :endDate="selectedChartInfo.endDate"
        />
        
      </div>
    </Popover>
  </div>
</template>


<script>
import moment from 'moment-timezone'
import Popover from '@/components/popover'
import DateUtils from '@/utils/dateUtils'

import InventoryStockLevelChartWidget from '@/modules/intelligence/dashboardWidgets/inventoryStockLevelChartWidget'

import { PurchaseOrderStatusTypeLookup, PurchaseOrderTypeLookup } from '@/store/types'

export default {
  name: 'PurchaseOrderReorderTable',
  components: {
    Popover,
    InventoryStockLevelChartWidget
  },
  props: {
    stockPurchasingList: {
      type: Array,
      required: true
    },
    usageStartDate: {
      type: String,
      required: false
    },
    usageEndDate: {
      type: String,
      required: false
    },
    incomingStartDate: {
      type: String,
      required: false
    },
    incomingEndDate: {
      type: String,
      required: false
    },
    blockOrderList: Array
  },
  computed: {
    incomingMonthList() {
      let startDate = this.incomingStartDate ? moment(this.incomingStartDate) : null
      let endDate = this.incomingEndDate ? moment(this.incomingEndDate) : null

      let monthList = []
      if(startDate && endDate) {
        let currentDate = startDate.clone().startOf('month')
        let endDateMonthStart = endDate.clone().startOf('month')

        while(currentDate.isSameOrBefore(endDateMonthStart)) {
          monthList.push({
            date: currentDate.clone(),
            month: currentDate.month(),
            year: currentDate.year(),
            label: currentDate.format('MMMM')
          })
          currentDate.add(1, 'month')
        }
      }      

      return monthList
    }
  },
  data() {
    return {
      selectedIncomingItemList: [],
      selectedIncomingInfo: {},
      selectedChartInfo: {},
      selectedContainerInfo: {},
      blockOrderEditingList: _.clone(this.blockOrderList || [])
    }
  },
  watch: {
    blockOrderList: function(newVal) {
      this.blockOrderEditingList = _.clone(newVal || [])
    }
  },
  created() {
    this.PurchaseOrderStatusTypeLookup = PurchaseOrderStatusTypeLookup
    this.PurchaseOrderTypeLookup = PurchaseOrderTypeLookup
  },
  methods: {
    displayIncomingStock(ev, date, stockPurchaseInfo) {
      this.selectedIncomingItemList = []
      this.selectedIncomingInfo = {
        stockId: stockPurchaseInfo.stockId,
        title: `Incoming Stock for ${stockPurchaseInfo.stockName} - ${date.format('MMMM YYYY')}`
      }

      if(stockPurchaseInfo && stockPurchaseInfo.incomingPOList) {
        let monthStart = date.clone().startOf('month')
        let monthEnd = date.clone().endOf('month')

        for(const poItem of stockPurchaseInfo.incomingPOList) {
          let expectedDate = moment(poItem.purchaseOrder.dateExpected)
          if(expectedDate.isSameOrAfter(monthStart) && expectedDate.isSameOrBefore(monthEnd)) {
            
            this.selectedIncomingItemList.push(poItem)
          }
        }
      }

      this.$refs.incomingStockPopover.displayFromElement(ev.target)
    },
    displayStockLevelChart(ev, stockPurchaseInfo) {
      this.selectedChartInfo = {
        stockId: stockPurchaseInfo.stockId,
        glassSizeId: stockPurchaseInfo.id,
        title: `Stock Level Chart for ${stockPurchaseInfo.stockName} - ${stockPurchaseInfo.height} x ${stockPurchaseInfo.width}`,
        startDate: moment(this.usageStartDate).toISOString(), // Start of the usage
        endDate: moment(this.incomingEndDate).toISOString()   // End of the Incoming
      }

      this.$refs.stockLevelChartPopover.displayFromElement(ev.target)
    },
    displayContainerInfo(ev, stockPurchaseInfo) {
      this.selectedContainerInfo = stockPurchaseInfo
      this.$refs.containerInfo.displayFromElement(ev.target)
    },
    getPurchaseRequiredRowClass(stockPurchaseInfo) {
      // No longer used. Looked ugly
      return ''
    },
    getPurchaseRequiredText(stockPurchaseInfo) {
      if(stockPurchaseInfo.requiresPurchasing)
        return 'Yes'

      if(stockPurchaseInfo.daysUntilPORequired !== undefined) {
        if(stockPurchaseInfo.daysUntilPORequired <= 14)
          return `In ${stockPurchaseInfo.daysUntilPORequired} days`
        else
          return `Soon`
      }

      return 'No'
    },
    getPurchaseRequiredIconClass(stockPurchaseInfo) {
      if(stockPurchaseInfo.requiresPurchasing)
        return 'fa-exclamation-triangle'

      if(stockPurchaseInfo.daysUntilPORequired !== undefined) {
        if(stockPurchaseInfo.daysUntilPORequired <= 14)
          return 'fa-exclamation-circle'
        else
          return 'fa-clock'
      }

      return null
    },
    getPurchaseRequiredLabelClass(stockPurchaseInfo) {
      if(stockPurchaseInfo.requiresPurchasing)
        return 'font-weight-bold text-danger'

      if(stockPurchaseInfo.daysUntilPORequired !== undefined) {
        if(stockPurchaseInfo.daysUntilPORequired <= 14)
          return 'font-weight-bold'
        else
          return `font-weight-bold`
      }

      return ''
    },
    getGlassSizeOrderBlockCount(glassSizeId) {
      let glassSizeItem = this.blockOrderEditingList.find(item => item.glassSizeId === glassSizeId)
      if(glassSizeItem)
        return glassSizeItem.blockCount
      return 0
    },
    updateGlassSizeOrderBlockCount(glassSizeId, blockCount) {
      let glassSizeItem = this.blockOrderEditingList.find(item => item.glassSizeId === glassSizeId)
      if(glassSizeItem)
        glassSizeItem.blockCount = blockCount
      else {
        glassSizeItem = {
          glassSizeId: glassSizeId,
          blockCount: blockCount
        }
        this.blockOrderEditingList.push(glassSizeItem)
      }

      this.$emit('blockOrderListUpdated', this.blockOrderEditingList)
    },
    isDateToday(date) {
      return DateUtils.isDateToday(date)
    },
    isDateBeforeToday(date) {
      return moment(date).isBefore(moment().local().startOf('day'))
    },
    stockTotalSheetsIncomingForMonth(date, stockPurchaseInfo) {
      let totalSheets = 0
      if(stockPurchaseInfo && stockPurchaseInfo.incomingPOList) {
        let monthStart = date.clone().startOf('month')
        let monthEnd = date.clone().endOf('month')

        for(const poItem of stockPurchaseInfo.incomingPOList) {
          let expectedDate = moment(poItem.purchaseOrder.dateExpected)
          if(expectedDate.isSameOrAfter(monthStart) && expectedDate.isSameOrBefore(monthEnd)) {
            totalSheets += poItem.totalSheets
          }
        }
      }

      return totalSheets
    }
    

  }
}
</script>